// src/components/forms/LoginForm.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWakingUp, setIsWakingUp] = useState(false);

 
  useEffect(() => {
    checkServerStatus();
  }, []);

  const checkServerStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('https://medigo-api.onrender.com/api/health', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.warn('⚠️ Server health check failed');
      } else {
        console.log('✅ Server is ready');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⏱️ Server may be sleeping (Render free tier)');
        setIsWakingUp(true);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setIsWakingUp(false);

    try {
        
      const result = await login({
        email: formData.email.toLowerCase().trim(),
        password: formData.password
      });


      if (result.success && result.data?.user) {
        const role = result.data.user.role;
        
        // Map roles to their dashboard routes
        const roleRoutes = {
          admin: '/admin',
          hospital: '/hospital',
          doctor: '/doctor',
          pharmacy: '/pharmacy',
          driver: '/driver',
          patient: '/patient',
          user: '/patient'
        };
        
        const redirectPath = roleRoutes[role] || '/patient';
        
        
        // Navigate immediately
        navigate(redirectPath, { replace: true });
      } else {
        console.error('❌ LoginForm: Login failed -', result.message);
        setIsSubmitting(false);
        
        // Check if it's a timeout error
        if (result.message && result.message.includes('timed out')) {
          setIsWakingUp(true);
          setSubmitError('The server is waking up (this can take 1-2 minutes on Render free tier). Please wait a moment and try again.');
        } else {
          setSubmitError(result.message || 'Login failed. Please check your credentials.');
        }
      }
    } catch (error) {
      console.error('❌ LoginForm: Login exception:', error);
      setIsSubmitting(false);
      
      // Check if it's a timeout error
      if (error.message && error.message.includes('timed out')) {
        setIsWakingUp(true);
        setSubmitError('The server is waking up (this can take 1-2 minutes on Render free tier). Please wait a moment and try again.');
      } else {
        setSubmitError(error.message || 'An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-indigo-100 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md lg:max-w-lg xl:max-w-xl p-6 sm:p-8 md:p-10">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Welcome Back</h2>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Sign in to your MediGo account
          </p>
        </div>
    
        
        <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
          {submitError && (
            <div className={`border rounded px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base ${
              isWakingUp 
                ? 'bg-yellow-50 border-yellow-200 text-yellow-700' 
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              <div className="flex items-start">
                {isWakingUp ? (
                  <Loader className="h-5 w-5 animate-spin flex-shrink-0 mt-0.5 mr-2" />
                ) : (
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 mr-2" />
                )}
                <span>{submitError}</span>
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1 sm:mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                disabled={isSubmitting || isLoading}
                className={`w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.email}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm sm:text-base font-medium text-gray-700 mb-1 sm:mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={isSubmitting || isLoading}
                className={`w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-2.5 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 disabled:opacity-50"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting || isLoading}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-gray-500" />
                ) : (
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 hover:text-gray-500" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                disabled={isSubmitting || isLoading}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="remember-me" className="ml-2 block text-xs sm:text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div className="text-xs sm:text-sm">
              <Link 
                to="/auth/forgot-password" 
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading || !formData.email || !formData.password}
            className="w-full bg-primary-600 text-white py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isSubmitting || isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isWakingUp ? 'Server Waking Up...' : 'Signing In...'}
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <div className="text-center">
            <span className="text-xs sm:text-sm text-gray-600">
              Don't have an account?{' '}
              <Link 
                to="/auth/register" 
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign up here
              </Link>
            </span>
            
          </div>
        
        </form>
      </div>
    </div>
  );
};

export default LoginForm;